import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, Dumbbell, Heart, AlertCircle, CheckCircle2 } from 'lucide-react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { wellnessAPI } from '../lib/api';

export default function LifestylePage() {
  const [lifestyle, setLifestyle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadLifestyleRecommendations();
  }, []);

  const loadLifestyleRecommendations = async () => {
    try {
      const response = await wellnessAPI.getLifestyle();
      setLifestyle(response.data.lifestyle);
    } catch (err) {
      setError('Failed to load lifestyle recommendations');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex flex-col items-center justify-center h-64">
          <div className="spinner mb-4" />
          <p className="text-[#4A2C2A]/60">Generating personalized recommendations...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="lifestyle-page">
        {/* Back Link */}
        <Link to="/dashboard" className="inline-flex items-center text-[#4A2C2A]/60 hover:text-[#4A2C2A]">
          <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
        </Link>

        {/* Header */}
        <div className="soft-card p-8 bg-gradient-to-br from-[#E8C495]/10 to-[#FDFBF7]">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#E8C495]/20 flex items-center justify-center">
              <Sun className="w-7 h-7 text-[#E8C495]" />
            </div>
            <div>
              <h1 className="font-heading text-3xl text-[#4A2C2A]">Lifestyle Recommendations</h1>
              <p className="text-[#4A2C2A]/60">Daily habits and routines for optimal wellness</p>
            </div>
          </div>
        </div>

        {error ? (
          <div className="soft-card p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#E8A095]" />
            <p className="text-[#4A2C2A]/70">{error}</p>
            <Button onClick={loadLifestyleRecommendations} className="mt-4">
              Try Again
            </Button>
          </div>
        ) : lifestyle ? (
          <>
            {/* Summary */}
            <div className="soft-card p-6">
              <h2 className="font-heading text-xl text-[#4A2C2A] mb-3">Overview</h2>
              <p className="text-[#4A2C2A]/70 leading-relaxed">{lifestyle.summary}</p>
            </div>

            {/* Categories Grid */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Sleep */}
              {lifestyle.sleep && (
                <div className="soft-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#95B8E8]/20 flex items-center justify-center">
                      <Moon className="w-5 h-5 text-[#95B8E8]" />
                    </div>
                    <h2 className="font-heading text-lg text-[#4A2C2A]">Sleep</h2>
                  </div>
                  {lifestyle.sleep.recommendations?.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {lifestyle.sleep.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A2C2A]/70">
                          <CheckCircle2 className="w-4 h-4 text-[#95B8E8] flex-shrink-0 mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  )}
                  {lifestyle.sleep.tips?.length > 0 && (
                    <div className="p-3 bg-[#FDFBF7] rounded-xl">
                      <p className="text-xs text-[#4A2C2A]/50 mb-2">Tips</p>
                      <ul className="space-y-1">
                        {lifestyle.sleep.tips.map((tip, i) => (
                          <li key={i} className="text-xs text-[#4A2C2A]/60">• {tip}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Exercise */}
              {lifestyle.exercise && (
                <div className="soft-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#8DA399]/20 flex items-center justify-center">
                      <Dumbbell className="w-5 h-5 text-[#8DA399]" />
                    </div>
                    <h2 className="font-heading text-lg text-[#4A2C2A]">Exercise</h2>
                  </div>
                  {lifestyle.exercise.recommendations?.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {lifestyle.exercise.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A2C2A]/70">
                          <CheckCircle2 className="w-4 h-4 text-[#8DA399] flex-shrink-0 mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  )}
                  {lifestyle.exercise.activities?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {lifestyle.exercise.activities.map((activity, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1 bg-[#8DA399]/10 text-[#4A2C2A] rounded-full text-xs"
                        >
                          {activity}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Stress Management */}
              {lifestyle.stress_management && (
                <div className="soft-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#E8A095]/20 flex items-center justify-center">
                      <Heart className="w-5 h-5 text-[#E8A095]" />
                    </div>
                    <h2 className="font-heading text-lg text-[#4A2C2A]">Stress Management</h2>
                  </div>
                  {lifestyle.stress_management.recommendations?.length > 0 && (
                    <ul className="space-y-2 mb-4">
                      {lifestyle.stress_management.recommendations.map((rec, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#4A2C2A]/70">
                          <CheckCircle2 className="w-4 h-4 text-[#E8A095] flex-shrink-0 mt-0.5" />
                          {rec}
                        </li>
                      ))}
                    </ul>
                  )}
                  {lifestyle.stress_management.techniques?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {lifestyle.stress_management.techniques.map((tech, i) => (
                        <span 
                          key={i}
                          className="px-3 py-1 bg-[#E8A095]/10 text-[#4A2C2A] rounded-full text-xs"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Daily Habits */}
              {lifestyle.daily_habits?.length > 0 && (
                <div className="soft-card p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-[#E8C495]/20 flex items-center justify-center">
                      <Sun className="w-5 h-5 text-[#E8C495]" />
                    </div>
                    <h2 className="font-heading text-lg text-[#4A2C2A]">Daily Habits</h2>
                  </div>
                  <ul className="space-y-2">
                    {lifestyle.daily_habits.map((habit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-[#4A2C2A]/70">
                        <CheckCircle2 className="w-4 h-4 text-[#E8C495] flex-shrink-0 mt-0.5" />
                        {habit}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Add to Reminders CTA */}
            <div className="soft-card p-6 border-l-4 border-[#8DA399]">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1 text-center sm:text-left">
                  <h3 className="font-heading text-lg text-[#4A2C2A] mb-1">Track Your Progress</h3>
                  <p className="text-sm text-[#4A2C2A]/60">
                    Add these recommendations as daily reminders to stay on track.
                  </p>
                </div>
                <Link to="/reminders">
                  <Button className="bg-[#8DA399] text-white hover:bg-[#7A9188] rounded-xl">
                    Manage Reminders
                  </Button>
                </Link>
              </div>
            </div>

            {/* CTA */}
            <div className="soft-card p-6 bg-gradient-to-r from-[#4A2C2A] to-[#2D1A19] text-white">
              <div className="flex flex-col md:flex-row items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-heading text-xl mb-2">Have Questions?</h3>
                  <p className="text-white/70 text-sm">
                    Chat with your wellness advisor for more personalized guidance.
                  </p>
                </div>
                <Link to="/chat">
                  <Button className="bg-[#E8A095] text-[#4A2C2A] hover:bg-[#D68C82] rounded-full px-6">
                    Ask Advisor
                  </Button>
                </Link>
              </div>
            </div>
          </>
        ) : (
          <div className="soft-card p-8 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-[#4A2C2A]/30" />
            <p className="text-[#4A2C2A]/70">No lifestyle recommendations available yet.</p>
            <p className="text-sm text-[#4A2C2A]/50 mt-2">
              Ask your wellness provider to upload relevant documents.
            </p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
